import { PROCEDURE_TYPE_LABELS, type ProcedureType } from '@/types/cgt'

/**
 * Pure formatting for the dashboard widgets (z8nrz7eqyj), kept out of the
 * components because only `.ts` files run under vitest here.
 */

export function procedureTypeLabel(type: ProcedureType | 'unknown'): string {
  return type === 'unknown' ? 'No type' : (PROCEDURE_TYPE_LABELS[type] ?? type)
}

/**
 * Where a run opens, or null when its type has no page of its own.
 *
 * A patient run opens on its patient (the transplant case page); MES and kit
 * runs have their own detail routes. A Transplant run with no MRN, and every
 * Cleaning or Inventory run, has nowhere to go, so the row is not a link.
 */
export function runHref(run: { id: string; type: ProcedureType | 'unknown'; patientMrn: string | null }): string | null {
  const id = encodeURIComponent(run.id)
  switch (run.type) {
    case 'Transplant':
      return run.patientMrn ? `/patients/${encodeURIComponent(run.patientMrn)}/procedures/${id}` : null
    case 'MES':
      return `/mes/orders/${id}`
    case 'KitCreation':
      return `/kits/runs/${id}`
    default:
      return null
  }
}

/** "3 days overdue", "Overdue today", "Due in 1 day", "Due today". */
export function atRiskText(row: { reason: 'overdue' | 'due_soon'; days: number }): string {
  const days = `${row.days} day${row.days === 1 ? '' : 's'}`
  if (row.reason === 'overdue') return row.days === 0 ? 'Overdue today' : `${days} overdue`
  return row.days === 0 ? 'Due today' : `Due in ${days}`
}

/** What a run is known by on screen: its work order, else its template. */
export function runTitle(run: { workOrderNumber: string | null; templateName: string }): string {
  return run.workOrderNumber ?? run.templateName
}

/** The open count for one type, 0 when the type has none open. */
export function openOfType(
  byType: readonly { type: ProcedureType | 'unknown'; count: number }[],
  type: ProcedureType,
): number {
  return byType.find((t) => t.type === type)?.count ?? 0
}

/** "Mon 5", from an ISO day, read as a calendar day (never shifted by timezone). */
export function shortDayLabel(isoDay: string): string {
  const [y, m, d] = isoDay.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return `${date.toLocaleDateString(undefined, { weekday: 'short' })} ${d}`
}

/** "5h late", "2d late": hours up to a day, then whole days. */
export function hoursLateText(hours: number): string {
  if (hours < 24) return `${hours}h late`
  return `${Math.floor(hours / 24)}d late`
}

/** "3 of 5 ea" — a quantity against a requirement, with its unit when known. */
export function qtyAgainst(have: number, need: number, uom: string | null): string {
  const n = (x: number) => (Number.isInteger(x) ? String(x) : x.toFixed(2))
  return `${n(have)} of ${n(need)}${uom ? ` ${uom}` : ''}`
}

export type Delta = { direction: 'up' | 'down' | 'flat'; text: string; good: boolean | null }

/**
 * A change against the previous period, for a stat tile.
 *
 * Signed percent when the previous period had something to compare with, the
 * plain difference when it was zero (a percent of zero is meaningless), and
 * flat when nothing moved. `good` follows the direction and whether up is good
 * for this number; null when flat or when neither direction is good or bad.
 */
export function deltaOf(current: number, previous: number, upIsGood: boolean | null = true): Delta {
  if (current === previous) return { direction: 'flat', text: 'No change', good: null }
  const direction = current > previous ? 'up' : 'down'
  const arrow = direction === 'up' ? '▲' : '▼'
  const text =
    previous === 0
      ? `${arrow} ${Math.abs(current - previous)}`
      : `${arrow} ${Math.round((Math.abs(current - previous) / previous) * 100)}%`
  return { direction, text, good: upIsGood === null ? null : (direction === 'up') === upIsGood }
}

/** What a period is compared with, in words: "yesterday", "previous 7 days". */
export function previousPeriodLabel(windowId: string): string {
  if (windowId === 'today') return 'yesterday'
  const days = Number.parseInt(windowId, 10)
  return Number.isFinite(days) ? `previous ${days} days` : 'previous period'
}
